import {
  Component,
  OnInit,
  HostBinding,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { views } from "@onsip/web/app/phone/views";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { take, map } from "rxjs/operators";
import { Router } from "@angular/router";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { RecordingService } from "@onsip/common/services/api/resources/recording/recording.service";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import { MatStepper } from "@angular/material/stepper";
import { AdvAnnouncementService } from "@onsip/common/services/api/resources/announcement/adv-announcement.service";
import { FormatSingularAddressTypePipe } from "@onsip/web/features/shared/pipes/format-singular-address-type.pipe";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AddressSummary } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";
import { AdvAnnouncement } from "@onsip/common/services/api/resources/announcement/adv-announcement";
import {
  ANNOUNCEMENTS_HANGUP_AOR,
  ANNOUNCEMENTS_HANGUP_OPTIONS
} from "../../addressAutocomplete/helpers/generate-announcements.config";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { TogglableRecorderComponent } from "@onsip/web/features/shared/components/togglableRecorder/togglable-recorder.component";
import { SafeResourceUrl } from "@angular/platform-browser";
import { RecordingTypesEnum } from "@onsip/web/features/shared/components/togglableRecorder/config/recording-types.enum";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { Recording } from "@onsip/common/services/api/resources/recording/recording";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-adv-announcements-setup",
  templateUrl: "./adv-announcements-setup.component.html",
  styleUrls: ["./adv-announcements-setup.component.scss"],
  providers: [FormatSingularAddressTypePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdvAnnouncementsSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  @ViewChild(TogglableRecorderComponent) recorderRef!: TogglableRecorderComponent;

  E2eLocators = E2eLocators;
  views = views;
  customOptions = ANNOUNCEMENTS_HANGUP_OPTIONS;

  showEditPanel = false;

  prevFormSnapshot = {
    announcementName: "",
    transferUsername: "",
    record: undefined as File | Recording | undefined
  };

  pricingText = "";

  transferToText = "";

  addressesArray!: Array<AddressSummary | undefined>;
  sanitizedAudioFile: SafeResourceUrl | undefined;
  recordingType: RecordingTypesEnum = RecordingTypesEnum.SELECT;

  loadingStateComplete$ = new BehaviorSubject(true);

  private sanitizedAudioFileBackup: SafeResourceUrl | undefined;
  private recordingTypeBackup: RecordingTypesEnum = RecordingTypesEnum.SELECT;

  announcementInfo = this.fb.group({
    announcementName: [
      "",
      [Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX), Validators.required]
    ],
    sipAddress: [""],
    extensions: [<Array<string>>[]],
    transferUsername: [""],
    record: [<File | Recording | undefined>undefined, [Validators.required]]
  });

  lessThanMediumBreakpoint = this.breakpointObserver
    .observe([Breakpoints.Small, Breakpoints.XSmall])
    .pipe(map(result => result.matches));

  mobileBreakpoint = this.breakpointObserver
    .observe([Breakpoints.XSmall])
    .pipe(map(result => result.matches));

  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  phonesList: Array<string> = [];
  currentAor = "";
  domain = "";

  private unsubscriber = new Subscription();
  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private recordingService: RecordingService,
    private snackbar: SnackbarService,
    private announcementService: AdvAnnouncementService,
    private singularAddressTypePipe: FormatSingularAddressTypePipe,
    private addressSummaryService: AddressSummaryService,
    private cdRef: ChangeDetectorRef,
    private dynamicPricing: DynamicPricingService,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.setPricing();
    this.initBrowse();
    this.initSubscriptions();
  }

  private initBrowse(): void {
    this.addressSummaryService.addressSummaryBrowse();
    this.announcementService.advAnnouncementBrowse();
    this.recordingService.recordingBrowse();
  }

  private initSubscriptions(): void {
    this.unsubscriber.add(
      combineLatest({
        addressArray: this.addressSummaryService.orgState,
        address: this.announcementInfo.controls.transferUsername.valueChanges
      })
        .pipe(
          map(({ addressArray, address }) => {
            this.addressesArray = [];
            addressArray.map(address => this.addressesArray.push(address));
            this.domain = addressArray[0].domain;
            return addressArray.find(add => add.username === this.getUserFromAddress(address));
          })
        )
        .subscribe(address => {
          this.setTransferText(address);
        })
    );

    this.unsubscriber.add(
      this.announcementInfo.controls.announcementName.valueChanges.subscribe(value => {
        if (value) {
          this.announcementInfo.patchValue({
            sipAddress: value
          });
        }
      })
    );
  }

  setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvAnnouncement", false).then(result => {
      this.pricingText = result;
      this.cdRef.markForCheck();
    });
  }

  getAnnouncementNameError(): string {
    const announcementName = this.announcementInfo.controls.announcementName;
    return announcementName.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS")
      : announcementName.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
        )
      : announcementName.hasError("required")
      ? this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED")
      : "";
  }

  handleCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Cancel setup?",
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.router.navigate([views.ADMIN_ADV_ANNOUNCEMENTS]);
          }
        })
    );
  }

  nextStep(isCustomStep = false): void {
    if (isCustomStep) {
      if (this.announcementInfo.controls.record.valid) {
        // complete the current step
        if (this.stepper.selected) this.stepper.selected.completed = true;
        // move to next step
        this.stepper.next();
      } else {
        this.showNoRecordingErrorSnackbar();
      }
      return;
    }
    if (this.announcementInfo.valid) {
      // complete the current step
      if (this.stepper.selected) this.stepper.selected.completed = true;
      // move to next step
      this.stepper.next();
    }
  }

  backStep(): void {
    if (this.announcementInfo.valid) {
      this.stepper.previous();
      if (this.showEditPanel) {
        this.announcementInfo.patchValue({ ...this.prevFormSnapshot });
        this.recordingType = this.recordingTypeBackup;
        this.sanitizedAudioFile = this.sanitizedAudioFileBackup;
        this.showEditPanel = !this.showEditPanel;
      }
    } else {
      if (this.announcementInfo.controls.record.invalid) {
        this.showNoRecordingErrorSnackbar();
      }
      this.announcementInfo.markAllAsTouched();
      this.announcementInfo.updateValueAndValidity();
    }
  }

  toggleEditPanel(isCancel = false): void {
    if (!this.showEditPanel) {
      this.prevFormSnapshot = {
        announcementName: this.announcementInfo.controls.announcementName.value,
        transferUsername: this.announcementInfo.controls.transferUsername.value,
        record: this.announcementInfo.controls.record.value
      };
      this.recordingTypeBackup = this.recordingType;
      this.sanitizedAudioFileBackup = this.sanitizedAudioFile;
      this.showEditPanel = !this.showEditPanel;
    } else {
      if (isCancel && this.prevFormSnapshot) {
        this.unsubscriber.add(
          this.dialog
            .open(ModalMaterialComponent, {
              panelClass: ["mat-typography", "onsip-dialog-universal-style"],
              data: {
                title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
                message: this.translate.instant(
                  "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
                ),
                primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
                secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
                primaryBtnFlat: true
              }
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(retObj => {
              if (retObj && retObj.doPrimaryAction) {
                // we want to revert changes if user clicks this
                this.announcementInfo.patchValue({ ...this.prevFormSnapshot });
                this.recordingType = this.recordingTypeBackup;
                this.sanitizedAudioFile = this.sanitizedAudioFileBackup;
                this.showEditPanel = !this.showEditPanel;
              }
            })
        );
      } else {
        if (this.announcementInfo.controls.record.valid) {
          this.showEditPanel = !this.showEditPanel;
        } else {
          this.showNoRecordingErrorSnackbar();
        }
      }
    }
  }

  showNoRecordingErrorSnackbar(): void {
    this.snackbar.openSnackBar("Please record, upload, or choose an existing recording.", "error");
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  handleAnnouncementAdd(result: ApiPromiseSubject<AdvAnnouncement>): void {
    if (result.status === "success") {
      this.currentAor = `${this.announcementInfo.controls.sipAddress.value}@${this.domain}`;
      this.extensionsListRef.editExtensions(this.announcementInfo.controls.sipAddress.value);

      this.snackbar.openSnackBar("Announcement successfully added.", "success");
      this.router.navigate([views.ADMIN_ADV_ANNOUNCEMENTS]);
    } else {
      this.snackbar.openSnackBar(result.data.message, "error");
      this.announcementService.clearErrors();
    }
    this.loadingStateComplete$.next(true);
  }

  setTransferText(address: AddressSummary | undefined): void {
    if (address) {
      const type = address.addressType;
      this.transferToText =
        "(" + this.singularAddressTypePipe.transform(type) + ") " + address.name;
    } else {
      this.transferToText = ANNOUNCEMENTS_HANGUP_AOR;
    }
  }

  getUserFromAddress(address: string): string {
    return address ? address.substring(0, address.indexOf("@")) : "";
  }

  showSaveErrorSnackbar(message: string): void {
    this.snackbar.openSnackBar(message, "error");
  }

  submit(): void {
    this.loadingStateComplete$.next(false);
    this.recorderRef
      .getRecNumber(this.announcementInfo.value.record, this.recordingType)
      .then(recNum => this.addAnnouncement(recNum));
  }

  addAnnouncement(recordNumber: string): void {
    this.announcementService
      .advAnnouncementAdd(
        this.announcementInfo.controls.announcementName.value,
        recordNumber,
        this.announcementInfo.controls.sipAddress.value,
        this.getUserFromAddress(this.announcementInfo.controls.transferUsername.value)
      )
      .then(result => this.handleAnnouncementAdd(result));
  }
}
